/**
 * ADR-0223 §3.3 — one idea (`/app/collab/ideas/:id`): the state token, the
 * title, the path (Raised → Discussed → Blocked / Decided / Set aside →
 * Became), the description and the chatter; on the side the blockers, where
 * it was raised, who can see it, and *Make it* (Initiative · Study · Report ·
 * Issue, or Set it aside…) — replaced once settled by the outcome and Reopen.
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { apiFetch } from "../lib/api.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { useToast } from "../components/Toast.js";
import { useConfirm } from "../components/ConfirmDialog.js";
import { CollabPage, errMessage, Thread, Tok, type Blocker, type Idea, type PathStep } from "../components/collab.js";

const STEP_CLASS: Record<PathStep["mark"], string> = { done: "collabStepDone", now_blocked: "collabStepNowBlocked", now_became: "collabStepNowBecame", now_aside: "collabStepNowAside", todo: "collabStepTodo" };

/** Pure, for the tests: the four steps across the sheet. */
export function PathView(props: { path: PathStep[] }) {
  return (
    <div className="collabPath">
      {props.path.map((s) => (
        <div className={`collabStep ${STEP_CLASS[s.mark]}`} key={s.step}>
          <span className="collabPin" />
          <span className="collabStepName">{s.label}</span>
          <span className="collabStepSub">{s.sub}</span>
        </div>
      ))}
    </div>
  );
}

export function BlockerCard(props: { b: Blocker; onRemove?: () => void }) {
  const { b } = props;
  return (
    <div className="collabBlocker">
      <Link className="t" to={b.href}>
        {b.title}
      </Link>
      <span className="m">
        <Tok token={b.status} label={b.status_label} />
        <span>{b.line}</span>
        {props.onRemove ? (
          <button type="button" className="collabLinkBtn isDanger" onClick={props.onRemove}>
            Unlink
          </button>
        ) : null}
      </span>
    </div>
  );
}

type PickItem = { id: string; label: string; sub?: string };

/** A small searchable list (§3.3): the tenant's open issues and action items, or the studies / reports / pages the person can open. */
export function Picker(props: { items: PickItem[]; placeholder: string; onPick: (id: string) => void; onCancel: () => void; loading?: boolean }) {
  const [q, setQ] = useState("");
  const shown = useMemo(() => {
    const n = q.trim().toLowerCase();
    return (n ? props.items.filter((i) => i.label.toLowerCase().includes(n)) : props.items).slice(0, 50);
  }, [props.items, q]);
  return (
    <div className="collabPicker">
      <input className="collabInput" type="search" placeholder={props.placeholder} value={q} onChange={(e) => setQ(e.target.value)} aria-label={props.placeholder} />
      <div className="collabPickerList">
        {props.loading ? <div className="collabEmpty">Loading…</div> : null}
        {!props.loading && shown.length === 0 ? <div className="collabEmpty">Nothing matches.</div> : null}
        {shown.map((i) => (
          <button type="button" key={i.id} onClick={() => props.onPick(i.id)}>
            {i.label}
            {i.sub ? <span className="collabHelp"> · {i.sub}</span> : null}
          </button>
        ))}
      </div>
      <button type="button" className="collabLinkBtn" onClick={props.onCancel}>
        Cancel
      </button>
    </div>
  );
}

type IssueRow = { id: string; title: string; status: string; assignee: { name: string } | null };
type ActionItemRow = { id: string; title: string; status: string };

/** What a session may raise by reference: the unsettled ideas, then the open issues and action items. */
export async function loadRaiseCandidates(): Promise<PickItem[]> {
  const [ideas, rest] = await Promise.all([
    apiFetch<{ ideas: Array<{ id: string; ref: string; title: string; state: string }> }>("/api/tenant/collab/ideas").catch(() => ({ ideas: [] })),
    loadBlockerCandidates(),
  ]);
  return [...ideas.ideas.filter((i) => i.state === "open" || i.state === "blocked").map((i) => ({ id: `idea:${i.id}`, label: i.title, sub: `Idea · ${i.ref}` })), ...rest];
}

/** The tenant's open issues and action items, as blocker candidates. */
export async function loadBlockerCandidates(): Promise<PickItem[]> {
  const [issues, items] = await Promise.all([
    apiFetch<{ issues: IssueRow[] }>("/api/tenant/issues?limit=500").catch(() => ({ issues: [] as IssueRow[] })),
    apiFetch<{ items: ActionItemRow[] }>("/api/tenant/action-items?limit=200").catch(() => ({ items: [] as ActionItemRow[] })),
  ]);
  const ai: ActionItemRow[] = items.items ?? [];
  return [
    ...issues.issues.map((i) => ({ id: `issue:${i.id}`, label: i.title, sub: `Issue · ${i.status}` })),
    ...ai.filter((a) => !["resolved", "dismissed"].includes(a.status)).map((a) => ({ id: `action_item:${a.id}`, label: a.title, sub: `Action item · ${a.status}` })),
  ];
}

export function CollabIdeaDetail(props: { entitlement?: string }) {
  void props;
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const [idea, setIdea] = useState<Idea | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ title: string; description: string } | null>(null);
  const [picker, setPicker] = useState<{ kind: "blocker" | "study" | "atlas_report" | "page"; items: PickItem[]; loading: boolean } | null>(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    if (!id) return;
    try {
      const r = await apiFetch<{ idea: Idea }>(`/api/tenant/collab/ideas/${encodeURIComponent(id)}`);
      setIdea(r.idea);
    } catch (e) {
      setError(errMessage(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    setLoading(true);
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function act(path: string, body: unknown, done?: string, method = "POST") {
    if (!idea || busy) return;
    setBusy(true);
    try {
      const r = await apiFetch<{ idea: Idea }>(`/api/tenant/collab/ideas/${encodeURIComponent(idea.id)}${path}`, { method, body });
      setIdea(r.idea);
      if (done) toast.push({ kind: "success", message: done });
    } catch (e) {
      toast.push({ kind: "error", message: errMessage(e) });
    } finally {
      setBusy(false);
    }
  }

  async function saveText() {
    if (!editing || !idea) return;
    const title = editing.title.trim();
    if (!title) return toast.push({ kind: "error", message: "A title is required." });
    await act("", { title, description: editing.description.trim() || null }, "Saved.", "PATCH");
    setEditing(null);
  }

  async function openPicker(kind: "blocker" | "study" | "atlas_report" | "page") {
    setPicker({ kind, items: [], loading: true });
    let items: PickItem[] = [];
    try {
      if (kind === "blocker") items = await loadBlockerCandidates();
      else if (kind === "study") items = (await apiFetch<{ studies: Array<{ id: string; slug: string; title: string }> }>("/api/tenant/studies")).studies.map((s) => ({ id: s.id, label: `${s.id} ${s.title}` }));
      else if (kind === "atlas_report") items = (await apiFetch<{ reports: Array<{ report_id: string; name: string }> }>("/api/tenant/atlas-reports")).reports.map((r) => ({ id: r.report_id, label: r.name, sub: "Atlas Report" }));
      else items = (await apiFetch<{ pages: Array<{ id: string; title: string }> }>("/api/tenant/pages?status=published").catch(() => ({ pages: [] }))).pages.map((p) => ({ id: p.id, label: p.title, sub: "Page" }));
    } catch (e) {
      toast.push({ kind: "error", message: errMessage(e) });
    }
    setPicker({ kind, items, loading: false });
  }

  async function pick(pickedId: string) {
    if (!picker) return;
    const kind = picker.kind;
    setPicker(null);
    if (kind === "blocker") {
      const [k, ref] = pickedId.split(":") as ["issue" | "action_item", string];
      await act("/blockers", k === "issue" ? { issue_id: ref } : { action_item_id: ref }, "Blocker linked.");
    } else {
      await act("/outcome", { kind, ref: pickedId }, "Recorded what it became.");
    }
  }

  async function setAside() {
    const reason = await confirm.confirmWithReason({ title: "Set this idea aside", detail: "The reason is kept with the idea so it is not raised again.", confirmLabel: "Set aside", reasonField: { label: "Why", placeholder: "The reason", maxLength: 500 } });
    if (reason) await act("/set-aside", { reason }, "Set aside.");
  }

  async function reopen() {
    const why = await confirm.confirmWithReason({ title: "Reopen this idea", detail: "The outcome or the reason it was set aside is cleared; why it was reopened is kept in its history.", confirmLabel: "Reopen", reasonField: { label: "Why", placeholder: "Why it is back", maxLength: 500 } });
    if (why) await act("/reopen", { why }, "Reopened.");
  }

  async function redact() {
    if (!idea) return;
    const reason = await confirm.confirmWithReason({ title: "Redact this idea", detail: "Its title and description read as removed on every surface; the record and its history stay.", confirmLabel: "Redact", destructive: true, reasonField: { label: "Reason", placeholder: "Why it is removed", maxLength: 2000 } });
    if (!reason) return;
    try {
      const r = await apiFetch<{ idea: Idea }>(`/api/tenant/admin/collab/ideas/${encodeURIComponent(idea.id)}/redact`, { method: "POST", body: { reason } });
      setIdea(r.idea);
    } catch (e) {
      toast.push({ kind: "error", message: errMessage(e) });
    }
  }

  async function postChatter(body: string) {
    if (!idea) return;
    try {
      await apiFetch(`/api/tenant/collab/ideas/${encodeURIComponent(idea.id)}/chatter`, { method: "POST", body: { body } });
      await load();
    } catch (e) {
      toast.push({ kind: "error", message: errMessage(e) });
    }
  }
  async function editChatter(messageId: string, body: string) {
    try {
      await apiFetch(`/api/tenant/collab/chatter/${encodeURIComponent(messageId)}`, { method: "PATCH", body: { body } });
      await load();
    } catch (e) {
      toast.push({ kind: "error", message: errMessage(e) });
    }
  }
  async function redactChatter(messageId: string) {
    const reason = await confirm.confirmWithReason({ title: "Redact this message", confirmLabel: "Redact", destructive: true, reasonField: { label: "Reason", placeholder: "Why it is removed", maxLength: 2000 } });
    if (!reason) return;
    try {
      await apiFetch(`/api/tenant/admin/collab/chatter/${encodeURIComponent(messageId)}/redact`, { method: "POST", body: { reason } });
      await load();
    } catch (e) {
      toast.push({ kind: "error", message: errMessage(e) });
    }
  }

  const crumb = (
    <span className="collabCrumb">
      <Link to="/app/collab/ideas">Ideas</Link> / {idea?.came_from.label ?? "…"}
    </span>
  );

  return (
    <CollabPage title={idea ? idea.ref : "Idea"} crumb={crumb}>
      {error ? <ErrorState detail={error} /> : null}
      {!error && loading ? <LoadingState /> : null}
      {!error && !loading && idea ? (
        <article className="collabSheet">
          <div className="collabSheetMain">
            <div style={{ display: "grid", gap: 14 }}>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <Tok token={idea.state} label={idea.state_label} />
                {idea.quiet && idea.quiet_since ? <span className="collabQuiet">Quiet since {idea.quiet_since}</span> : null}
                {idea.can.edit && !editing ? (
                  <button type="button" className="collabLinkBtn" style={{ marginLeft: "auto" }} onClick={() => setEditing({ title: idea.title, description: idea.description ?? "" })}>
                    Edit
                  </button>
                ) : null}
              </div>
              {editing ? (
                <div className="collabForm" style={{ maxWidth: "none" }}>
                  <input className="collabInput" value={editing.title} maxLength={200} onChange={(e) => setEditing({ ...editing, title: e.target.value })} aria-label="Title" />
                  <textarea className="collabInput" rows={6} value={editing.description} maxLength={10000} onChange={(e) => setEditing({ ...editing, description: e.target.value })} aria-label="Description" />
                  <div className="collabPost" style={{ justifyContent: "flex-start" }}>
                    <button type="button" className="collabBtn collabBtnAmber" disabled={busy} onClick={() => void saveText()}>
                      Save
                    </button>
                    <button type="button" className="collabBtn" onClick={() => setEditing(null)}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <h1 className="collabIdeaTitle">{idea.title}</h1>
              )}
            </div>
            <PathView path={idea.path} />
            {idea.description && !editing ? <p className="collabIdeaBody">{idea.description}</p> : null}
            <section className="collabPart">
              <div className="collabPartHead">
                <h2 className="collabH2">Chatter</h2>
                <span>{idea.chatter.length}</span>
              </div>
              <Thread messages={idea.chatter} prompt="Add to the chatter…" onPost={postChatter} onEdit={editChatter} onRedact={idea.can.redact ? redactChatter : undefined} />
            </section>
          </div>
          <aside className="collabSheetSide">
            <div className="collabFacts">
              {idea.blockers.length > 0 ? (
                <div className="collabFact">
                  <div className="k">Blocked by</div>
                  <div style={{ display: "grid", gap: 8 }}>
                    {idea.blockers.map((b) => (
                      <BlockerCard key={b.id} b={b} onRemove={idea.can.link_blocker ? () => void act(`/blockers/${encodeURIComponent(b.id)}`, undefined, "Unlinked.", "DELETE") : undefined} />
                    ))}
                  </div>
                </div>
              ) : null}
              {idea.can.link_blocker ? (
                picker?.kind === "blocker" ? (
                  <Picker items={picker.items} loading={picker.loading} placeholder="An open issue or action item" onPick={(x) => void pick(x)} onCancel={() => setPicker(null)} />
                ) : (
                  <div>
                    <button type="button" className="collabLinkBtn" onClick={() => void openPicker("blocker")}>
                      + Link an issue or action item that blocks it
                    </button>
                  </div>
                )
              ) : null}
              <div className="collabFact">
                <div className="k">Raised</div>
                <div className="v">
                  {idea.raised_by?.name ?? "A former member"}, {idea.path[0]?.sub.split(", ")[0]}
                  <br />
                  <span className="collabSideText">{idea.came_from.session_id ? <Link to={`/app/collab/sessions/${idea.came_from.session_id}`}>{idea.came_from.label}</Link> : idea.came_from.label}</span>
                </div>
              </div>
              <div className="collabFact">
                <div className="k">Who can see it</div>
                <div className="v">{idea.visibility_line}</div>
              </div>
              {idea.can.make ? (
                <div className="collabFact">
                  <div className="k">Make it</div>
                  {picker && picker.kind !== "blocker" ? (
                    <Picker items={picker.items} loading={picker.loading} placeholder={picker.kind === "study" ? "A study you can open" : picker.kind === "atlas_report" ? "An Atlas Report" : "A page"} onPick={(x) => void pick(x)} onCancel={() => setPicker(null)} />
                  ) : (
                    <>
                      <div className="collabMake">
                        <button type="button" className="collabBtn" onClick={() => navigate("/app/initiatives/new", { state: { from_idea: { id: idea.id, title: idea.title, description: idea.description ?? "" } } })}>
                          Initiative
                        </button>
                        <button type="button" className="collabBtn" onClick={() => void openPicker("study")}>
                          Study
                        </button>
                        <button type="button" className="collabBtn" onClick={() => void openPicker("atlas_report")}>
                          Report
                        </button>
                        <button type="button" className="collabBtn" onClick={() => navigate("/app/issues/new", { state: { from_idea: { id: idea.id, title: idea.title, description: idea.description ?? "" } } })}>
                          Issue
                        </button>
                      </div>
                      <div style={{ marginTop: 8, display: "flex", gap: 14 }}>
                        <button type="button" className="collabLinkBtn" onClick={() => void openPicker("page")}>
                          A page…
                        </button>
                        <button type="button" className="collabLinkBtn" onClick={() => void setAside()}>
                          Set it aside…
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ) : null}
              {idea.outcome ? (
                <div className="collabFact">
                  <div className="k">Became</div>
                  <div className="v">{idea.outcome.href ? <Link to={idea.outcome.href}>{idea.outcome.label}</Link> : idea.outcome.label}</div>
                </div>
              ) : null}
              {idea.set_aside ? (
                <div className="collabFact">
                  <div className="k">Set aside</div>
                  <div className="v">
                    {idea.set_aside.reason}
                    {idea.set_aside.by ? <span className="collabSideText"> — {idea.set_aside.by.name}</span> : null}
                  </div>
                </div>
              ) : null}
              {idea.can.reopen ? (
                <div>
                  <button type="button" className="adminNewLink" onClick={() => void reopen()}>
                    Reopen
                  </button>
                </div>
              ) : null}
              {idea.can.redact ? (
                <div>
                  <button type="button" className="collabLinkBtn isDanger" onClick={() => void redact()}>
                    Redact
                  </button>
                </div>
              ) : null}
            </div>
          </aside>
        </article>
      ) : null}
    </CollabPage>
  );
}
